import React, { useState } from 'react';
import { aiAPI } from '../api';
import { LANGUAGE_LABELS } from '../utils/clientTexts';

/**
 * Small AI-assist button that appears on non-EN language tabs.
 *
 * Props:
 *   - targetLang: 'pl' | 'de' — the language we'll fill
 *   - getSourceTexts: () => ({ key: string }) — returns the EN text bucket
 *   - getTargetTexts: () => ({ key: string }) — returns the current target-lang bucket (used for "only fill empty" mode)
 *   - onTranslated: ({ key: translated }) => void — called with the AI output
 *   - testId: optional data-testid
 *
 * Uses the existing /api/ai/translate-texts endpoint (Claude Sonnet 4.5 via
 * Emergent LLM Universal Key).
 */
const TranslateFromEnButton = ({ targetLang, getSourceTexts, getTargetTexts, onTranslated, testId, t = {} }) => {
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState(null);
  const [onlyEmpty, setOnlyEmpty] = useState(false);

  if (targetLang === 'en') return null;

  const handleClick = async () => {
    const source = getSourceTexts() || {};
    const target = (getTargetTexts ? getTargetTexts() : null) || {};

    // Send only non-empty EN values; when "only empty" is on, also skip keys
    // that the user has already translated in the target bucket.
    //
    // NOTE: This button is used in the admin TemplateEditor, where `""`
    // semantically means "no template default" (no intentional clear). For
    // the user-side workspace branding flow, "intentional empty" is handled
    // separately inside `UserSettings.handleSaveBranding` — we don't expose
    // this button there.
    const payload = Object.fromEntries(
      Object.entries(source).filter(([k, v]) => {
        if (typeof v !== 'string' || !v.trim()) return false;
        if (onlyEmpty) {
          const existing = target[k];
          if (typeof existing === 'string' && existing.trim()) return false;
        }
        return true;
      })
    );

    if (Object.keys(payload).length === 0) {
      setHint(onlyEmpty
        ? (t.translate_nothing_to_fill || 'Nothing to fill — every field already has a translation.')
        : (t.translate_fill_en_first || 'Fill the EN tab first — nothing to translate.'));
      setTimeout(() => setHint(null), 3500);
      return;
    }
    setLoading(true);
    setHint(null);
    try {
      const { data } = await aiAPI.translateTexts('en', targetLang, payload);
      const translations = data?.translations || {};
      if (Object.keys(translations).length === 0) {
        setHint(t.translate_no_results || 'Translator returned no results.');
      } else {
        onTranslated(translations);
        const n = Object.keys(translations).length;
        const tmpl = t.translate_translated_n_to || 'Translated {n} field{s} to {lang}.';
        setHint(tmpl
          .replace('{n}', n)
          .replace('{s}', n !== 1 ? 's' : '')
          .replace('{lang}', LANGUAGE_LABELS[targetLang]));
      }
    } catch (err) {
      setHint(err.response?.data?.detail || (t.translate_failed || 'Translation failed.'));
    } finally {
      setLoading(false);
      setTimeout(() => setHint(null), 4500);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid={testId || `translate-from-en-${targetLang}`}
        style={{
          fontSize: '11px',
          textTransform: 'uppercase',
          padding: '7px 14px',
          border: '1px solid var(--kahla-taupe, #c7c0b3)',
          background: loading ? 'var(--kahla-cream-light)' : 'transparent',
          color: 'var(--kahla-charcoal)',
          cursor: loading ? 'wait' : 'pointer',
          fontFamily: 'inherit',
          letterSpacing: '0.08em',
          transition: 'all 0.2s ease',
        }}
      >
        {loading
          ? (t.translating_short || 'Translating…')
          : `✦  ${(t.translate_from_en_to || 'Translate from EN →')} ${targetLang.toUpperCase()}`}
      </button>

      <label
        data-testid={`${testId || `translate-from-en-${targetLang}`}-only-empty`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          fontSize: '11px', color: 'var(--kahla-text-light)',
          cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        }}
      >
        <input
          type="checkbox"
          checked={onlyEmpty}
          onChange={e => setOnlyEmpty(e.target.checked)}
          style={{ cursor: 'pointer' }}
        />
        {t.only_fill_empty || 'Only fill empty fields'}
      </label>

      {hint && (
        <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)', fontStyle: 'italic' }}>
          {hint}
        </span>
      )}
    </div>
  );
};

export default TranslateFromEnButton;
